import { normalizeColumnType } from "../../../utils/normalizeColumnType";
import type { LinkedBoard } from "../table.data.interface";

export interface BoardColumnInfo {
  id: string;
  title: string;
  type: string;
  settings?: any;
}

export type BoardColumnsById = Record<string, BoardColumnInfo[]>;

export interface ColumnIdentity {
  title: string;
  type: string;
}

/** A linked board that already holds the column, with that board's own column id. */
export interface ColumnHolder {
  board: LinkedBoard;
  column: BoardColumnInfo;
}

// monday can create these, but an empty copy doesn't sync: the user has to connect it to a
// board themselves, so the item view points them at setup help instead of offering "+".
const MANUAL_SETUP_TYPES = new Set(["board_relation"]);

export const requiresManualSetup = (columnType: string): boolean =>
  MANUAL_SETUP_TYPES.has(normalizeColumnType(columnType));

export const isSameType = (a: string, b: string): boolean =>
  normalizeColumnType(a) === normalizeColumnType(b);

/** SIMB syncs on an exact name + type match, so that is what "has the column" means here. */
export const findMatchingColumn = (
  columns: BoardColumnInfo[] | undefined,
  identity: ColumnIdentity,
): BoardColumnInfo | undefined =>
  columns?.find(
    (column) => column.title === identity.title && isSameType(column.type, identity.type),
  );

/** A same-titled column of another type: creating ours beside it would never sync cleanly. */
export const findTitleConflict = (
  columns: BoardColumnInfo[] | undefined,
  title: string,
  type: string,
): BoardColumnInfo | undefined =>
  columns?.find((column) => column.title === title && !isSameType(column.type, type));

export const splitBoardsByColumn = (
  linkedBoards: LinkedBoard[],
  columnsByBoardId: BoardColumnsById,
  identity: ColumnIdentity,
): { holders: ColumnHolder[]; missing: LinkedBoard[] } => {
  const holders: ColumnHolder[] = [];
  const missing: LinkedBoard[] = [];

  linkedBoards.forEach((board) => {
    const column = findMatchingColumn(columnsByBoardId[board.boardId], identity);
    if (column) holders.push({ board, column });
    else missing.push(board);
  });

  return { holders, missing };
};

/** The board whose value gets copied: the one the user is looking at when it has the column. */
export const pickSourceHolder = (
  holders: ColumnHolder[],
  currentBoardId: string,
): ColumnHolder | undefined =>
  holders.find((holder) => holder.board.boardId === currentBoardId) ?? holders[0];

export interface NameValidation {
  /** Blocks confirm. Null when the name is usable everywhere it will land. */
  error: string | null;
  /** boardId -> reason, for target boards that can't take the column under this name. */
  conflictsByBoardId: Record<string, string>;
}

const describeType = (type: string) => normalizeColumnType(type).replace(/_/g, " ");

/**
 * Checks `name` against every board it will end up on: the picked targets (where the column
 * is created) and, when it differs from the current title, the holders (where it is renamed).
 */
export const validateColumnName = ({
  name,
  identity,
  holders,
  selectedTargets,
  columnsByBoardId,
}: {
  name: string;
  identity: ColumnIdentity;
  holders: ColumnHolder[];
  selectedTargets: LinkedBoard[];
  columnsByBoardId: BoardColumnsById;
}): NameValidation => {
  const trimmed = name.trim();
  const conflictsByBoardId: Record<string, string> = {};

  if (!trimmed) return { error: "Enter a column name.", conflictsByBoardId };

  selectedTargets.forEach((board) => {
    const conflict = findTitleConflict(columnsByBoardId[board.boardId], trimmed, identity.type);
    if (conflict) {
      conflictsByBoardId[board.boardId] =
        `Already has a ${describeType(conflict.type)} column named "${trimmed}"`;
    }
  });

  if (trimmed !== identity.title) {
    const blockedHolder = holders.find(({ board, column }) =>
      columnsByBoardId[board.boardId]?.some(
        (other) => other.id !== column.id && other.title === trimmed,
      ),
    );
    if (blockedHolder) {
      return {
        error: `${blockedHolder.board.boardName} already has a column named "${trimmed}". Pick another name.`,
        conflictsByBoardId,
      };
    }
  }

  if (Object.keys(conflictsByBoardId).length > 0) {
    return {
      error: "Rename the column, or untick the boards that already use this name.",
      conflictsByBoardId,
    };
  }

  return { error: null, conflictsByBoardId };
};
